import { Box, Button, CircularProgress, IconButton, MenuItem, Snackbar, TextField, Typography } from '@mui/material';
import Header from '../../../components/Header';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { useTheme } from '@emotion/react';
import { tokens } from '../../../theme';

const ImportGuides = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({ country: '' });
  const [country, setCountry] = useState([]);
  const [file, setFile] = useState(null);
  const token = localStorage.getItem('token');

  const getCountryNames = async () => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/countrynames`, { headers: { token } });
    const data = await res.json();
    setCountry(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    getCountryNames();
  }, []);

  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setOpenToast(true);
      setToastmessage('Please select a file to upload.');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    if (values.country) {
      formData.append('country', values.country);
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/guides/import`, {
        headers: { token },
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      setOpenToast(false);
      if (data.message) {
        setOpenToast(true);
        setToastmessage(data.message);
        if (data.success) {
          setTimeout(() => {
            navigate('/guide');
          }, 2000);
        }
      }
    } catch (error) {
      console.error(error);
      setOpenToast(true);
      setToastmessage('An error occurred while importing guides.');
    }

    setLoading(false);
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  const handleDownloadSample = () => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL || ''}/guide-import-sample.csv`;
    link.download = 'guide-import-sample.csv';
    link.click();
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" rowGap="10px" flexWrap="wrap-reverse">
        <Header title="IMPORT GUIDES" subtitle="Import guide details using Excel or CSV" />

        <Box>
          <Link to={-1}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.blueAccent[800],
                },
              }}
            >
              Go Back
            </Button>
          </Link>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Box display="flex" flexDirection="column" gap="10px">
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Country (optional)"
            onChange={handleChange}
            value={values.country}
            name="country"
            select
          >
            <MenuItem value="">None</MenuItem>
            {country &&
              country.map((c) => (
                <MenuItem key={c.CountryID} value={c.CountryID}>
                  {c.CountryName}
                </MenuItem>
              ))}
          </TextField>

          <Box
            sx={{
              py: 2.5,
              px: 2.5,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[600]} 100%)`,
              border: `1px solid ${colors.blueAccent[600]}`,
              boxShadow: `0 4px 20px ${colors.primary[900]}`,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1.5,
                color: colors.blueAccent[300],
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Required Fields
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, color: colors.grey[200], fontSize: '0.875rem', lineHeight: 1.8, mb: 2 }}>
              <li><strong>name</strong> — First name</li>
              <li><strong>surname</strong> — Last name</li>
              <li><strong>gender</strong> — M, F, or O</li>
              <li><strong>dateOfBirth</strong> — e.g., 1990-05-15 or 15.05.1990</li>
              <li><strong>email</strong> — Must be unique</li>
              <li><strong>phone</strong> — e.g., +381601234567</li>
              <li><strong>address</strong> — Full address</li>
              <li><strong>license</strong> — License ID</li>
            </Box>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                color: colors.blueAccent[300],
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Optional Fields
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, color: colors.grey[200], fontSize: '0.875rem', lineHeight: 1.8, mb: 2 }}>
              <li><strong>country</strong> — Country ID or name</li>
              <li><strong>vehicleNo</strong> — Vehicle number</li>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
              onClick={handleDownloadSample}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                fontWeight: 600,
                fontSize: '12px',
                px: 2,
                py: 1,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: colors.greenAccent[500],
                },
              }}
            >
              Download Sample CSV
            </Button>
          </Box>

          <label
            htmlFor="file"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '15px 30px',
              background: colors.primary[400],
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer',
              borderRadius: '10px',
            }}
          >
            <CloudUploadIcon /> {file ? file.name : 'Choose file*'}
          </label>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            id="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            hidden
          />
          <Button
            type="submit"
            disabled={loading}
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              width: 'fit-content',
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 30px',
              '&:hover': {
                backgroundColor: colors.blueAccent[800],
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress color="secondary" size="20px" sx={{ marginRight: '10px' }} />
                Uploading
              </>
            ) : (
              'Upload File'
            )}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ImportGuides;
